import { useConnectionStore } from '../../lib/stomp';
import type { Field } from '../../lib/templates';
import { usePresence } from '../../store/presenceStore';

interface PresenceBarProps {
  roomId: number;
  fields: Field[];
}

/** Initials for the avatar chip: first letters of up to two space-separated words, or the first
 * two characters of a single word — Discord usernames are rarely more than one word, but this
 * degrades sensibly either way. */
function initials(username: string): string {
  const parts = username.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
  }
  return (parts[0] ?? '').slice(0, 2).toUpperCase();
}

/** DESIGN.md: `LIVE LINK` lime dot / `RECONNECTING…` coral blink / `OFFLINE`. */
function ConnectionIndicator() {
  const status = useConnectionStore((state) => state.status);

  if (status === 'connected') {
    return (
      <span className="telemetry flex items-center gap-1.5 text-accent">
        <span className="block size-1.5 rounded-full bg-accent" aria-hidden="true" />
        Live link
      </span>
    );
  }
  if (status === 'reconnecting') {
    return (
      <span className="telemetry flex items-center gap-1.5 text-signal" role="status">
        <span className="block size-1.5 rounded-full bg-signal blink" aria-hidden="true" />
        Reconnecting…
      </span>
    );
  }
  return (
    <span className="telemetry flex items-center gap-1.5 text-text-faint">
      <span className="block size-1.5 rounded-full bg-text-faint" aria-hidden="true" />
      Offline
    </span>
  );
}

/**
 * Who's here (lime-outline initials chips, ≥44px, tooltip naming the field they're editing) plus
 * the connection indicator, per DESIGN.md. Lives in the room header (see `routes/Room.tsx`).
 */
export function PresenceBar({ roomId, fields }: PresenceBarProps) {
  const presence = usePresence(roomId);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <ConnectionIndicator />
      {presence.length > 0 && (
        <ul className="flex flex-wrap items-center gap-1.5" aria-label="Who's here">
          {presence.map((person) => {
            const fieldLabel = person.focus
              ? (fields.find((field) => field.id === person.focus?.fieldId)?.label ?? null)
              : null;
            const title = fieldLabel ? `${person.username} is editing ${fieldLabel}` : person.username;
            return (
              <li key={person.userId}>
                <span
                  title={title}
                  aria-label={title}
                  className="eyebrow flex min-h-tap min-w-tap items-center justify-center rounded-full border border-accent-border px-2 text-accent"
                >
                  {initials(person.username)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
